import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import {
  ConditionType,
  diabetesTypeColorMapping,
  diabetesTypeTranslationMapping,
  type MedicationSchema,
} from "@/models/ConditionModel.ts";
import { PatientListItem } from "@/models/PatientSummaryModel.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type DiabetesTypeColumnProps = CellContext<PatientListItem, ConditionType>;

export const DiabetesTypeColumn: React.FC<DiabetesTypeColumnProps> = ({ row }) => {
  const { t } = useTranslation();
  const type = row.original.diabetesType;
  const insulin = row.original.insulinTreatmentType;
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.container}>
      <Tag label={t(diabetesTypeTranslationMapping(type, insulin))} color={diabetesTypeColorMapping(type, insulin)} />
    </div>
  );
};

export const DiabetesTypeHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.diabetesType")}</Typography>
    </div>
  );
};

const diabetesTypeRank = (type: ConditionType, insulin: MedicationSchema) => {
  switch (true) {
    case type === "gestational" && insulin === undefined:
      return 0;
    case type === "gestational" && insulin === "insulinPen":
      return 1;
    case type === "gestational" && insulin === "noInsulin":
      return 2;
    case type === "type2":
      return 3;
    case type === "type1":
      return 4;
    default:
      return 5;
  }
};

export const sortByDiabetesType: SortingFn<PatientListItem> = (rowA, rowB) =>
  diabetesTypeRank(rowA.original.diabetesType, rowA.original.insulinTreatmentType) -
  diabetesTypeRank(rowB.original.diabetesType, rowB.original.insulinTreatmentType);

const makeStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});

const makeHeaderStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
